"use client";

import { Button } from "@rallly/ui/button";
import { Card } from "@rallly/ui/card";
import { CalendarIcon, LockIcon } from "lucide-react";
import * as React from "react";
import { createBreakpoint } from "react-use";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateIcon,
  EmptyStateTitle,
} from "@/components/empty-state";
import { AddToCalendarButton } from "@/features/calendars/components/add-to-calendar-button";
import { usePoll } from "@/features/poll/client";
import DesktopPoll from "@/features/poll/components/desktop-poll";
import MobilePoll from "@/features/poll/components/mobile-poll";
import {
  EventDate,
  EventTimeRange,
} from "@/features/scheduled-event/components/event-date-time";
import { Trans } from "@/i18n/client";

const useBreakpoint = createBreakpoint({ list: 320, table: 640 });

function ScheduledDateTime({
  start,
  duration,
  timeZone,
}: {
  start: Date;
  duration: number;
  timeZone: string | null;
}) {
  const allDay = duration === 0;

  return (
    <span className="flex flex-col items-center gap-0.5">
      <EventDate
        value={start}
        allDay={allDay}
        timeZone={timeZone}
        preset="dateFull"
        className={allDay ? undefined : "text-foreground"}
      />
      <EventTimeRange
        start={start}
        end={new Date(start.getTime() + duration * 60_000)}
        allDay={allDay}
        timeZone={timeZone}
        showTimeZone
      />
    </span>
  );
}

export function ResponsiveResults() {
  const poll = usePoll();
  const breakpoint = useBreakpoint();
  const [dismissed, setDismissed] = React.useState(false);
  const PollComponent = breakpoint === "table" ? DesktopPoll : MobilePoll;

  if (!dismissed && poll.status === "scheduled" && poll.event) {
    return (
      <Card>
        <EmptyState>
          <EmptyStateIcon>
            <CalendarIcon />
          </EmptyStateIcon>
          <EmptyStateTitle>
            <Trans i18nKey="pollStatusScheduled" defaults="Scheduled" />
          </EmptyStateTitle>
          <EmptyStateDescription>
            <ScheduledDateTime
              start={poll.event.start}
              duration={poll.event.duration}
              timeZone={poll.timeZone}
            />
          </EmptyStateDescription>
          <EmptyStateFooter className="flex flex-wrap items-center justify-center gap-2">
            <AddToCalendarButton eventId={poll.event.id} />
            <Button onClick={() => setDismissed(true)}>
              <Trans i18nKey="viewResults" defaults="View results" />
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </Card>
    );
  }

  if (!dismissed && poll.status === "closed") {
    return (
      <Card>
        <EmptyState>
          <EmptyStateIcon>
            <LockIcon />
          </EmptyStateIcon>
          <EmptyStateTitle>
            <Trans i18nKey="pollStatusClosed" defaults="Closed" />
          </EmptyStateTitle>
          <EmptyStateDescription>
            {poll.closedReason === "auto" ? (
              <Trans
                i18nKey="pollAutoClosedDescription"
                defaults="This poll was closed automatically because all of its dates have passed. No more responses are being accepted."
              />
            ) : (
              <Trans
                i18nKey="pollClosedDescription"
                defaults="No more responses are being accepted."
              />
            )}
          </EmptyStateDescription>
          <EmptyStateFooter>
            <Button onClick={() => setDismissed(true)}>
              <Trans i18nKey="viewResults" defaults="View results" />
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </Card>
    );
  }

  return <PollComponent />;
}
